/*
 * WorkAndBuhin.vue の呼び出し元ごとの差異を吸収するため、data の形状正規化と現在作業の解決を切り出した処理。
 */
import { INSPECT_TYPE } from "@/model/InspectType";

export type WorkBuhinSourceMode =
  | "inspect"
  | "inspectLine"
  | "recommend"
  | "jizenRecommend"
  | "partSelectionInspect"
  | "partSelectionRecommend";

export interface WorkBuhinNormalizeOptions {
  mode: WorkBuhinSourceMode;
  selectedWorkIndex?: number;
  isManualWork?: boolean;
  sourceCategory?: any;
}

export interface WorkBuhinInputContext {
  mode: WorkBuhinSourceMode;
  source: any;
  sourceCategory?: any;
  inspectItem?: any;
  recommendItem?: any;
  seibiList: any[];
  manualSeibiList: any[];
  activeWork: any | null;
  selectedWorkIndex: number;
  isManualWork: boolean;
  buhinList: any[];
  osusumeBuhinList: any[];
  manualBuhinList: any[];
  hasSeibiList: boolean;
  isRecommend: boolean;
}

export interface WorkBuhinCurrentWorkResolution {
  work: any;
  activeWorkIndex: number;
  shouldUpdateActiveWorkIndex: boolean;
}

export interface WorkBuhinCurrentWorkEffectState {
  shouldUpdateActiveWorkIndex: boolean;
  activeWorkIndex: number;
  work: any;
}

export interface WorkBuhinContextUpdateState {
  shouldUpdateStoredContext: boolean;
}

/*
 * resolveWorkBuhinSourceMode を WorkAndBuhin.vue から分離し、
 * 呼び出し元データの形から normalizeWorkBuhinInput の mode を選ぶ条件を固定するため。
 */
export function resolveWorkBuhinSourceMode(params: {
  data: any;
  editSeibi: boolean;
}): WorkBuhinSourceMode {
  const data = params.data;
  if (
    data &&
    data.seibiList &&
    !data.sknInspectDescriptionId &&
    !data.sknUketsukeInspectId &&
    !data.inspectResult
  ) {
    return "partSelectionRecommend";
  }
  if (data && data.sknInspectType == INSPECT_TYPE.INSPECT_LINE) {
    return "inspectLine";
  }
  if (!params.editSeibi) {
    return "recommend";
  }
  return "inspect";
}

/*
 * buildWorkBuhinNormalizeOptions を WorkAndBuhin.vue から分離し、
 * 現在の選択 index と手入力状態を normalizeWorkBuhinInput の options に変換するため。
 */
export function buildWorkBuhinNormalizeOptions(params: {
  data: any;
  editSeibi: boolean;
  activeWorkIndex: number;
  isManualWork: boolean;
}): WorkBuhinNormalizeOptions {
  return {
    mode: resolveWorkBuhinSourceMode({
      data: params.data,
      editSeibi: params.editSeibi
    }),
    selectedWorkIndex: params.activeWorkIndex > -1
      ? params.activeWorkIndex
      : undefined,
    isManualWork: params.isManualWork
  };
}

/*
 * buildWorkBuhinContextUpdateState を WorkAndBuhin.vue から分離し、
 * refreshWorkBuhinContext で親 component の保持 context を更新する条件を固定するため。
 */
export function buildWorkBuhinContextUpdateState(params: {
  data: any;
  currentData: any;
}): WorkBuhinContextUpdateState {
  return {
    shouldUpdateStoredContext: params.data === params.currentData
  };
}

/*
 * normalizeWorkBuhinInput を WorkAndBuhin.vue から分離し、
 * 入力形状の正規化と現在作業解決 を親側置換や単体テストで固定するため。
 */
export function normalizeWorkBuhinInput(
  source: any,
  options: WorkBuhinNormalizeOptions
): WorkBuhinInputContext {
  const mode = options.mode;
  const seibiList = getSeibiList(source, mode);
  const manualSeibiList = toArray(source && source.manualSeibiList);
  const selected = resolveSelectedWork(
    source,
    mode,
    seibiList,
    manualSeibiList,
    options
  );
  const activeWork = selected.activeWork
    ? normalizeWorkBuhinWork(selected.activeWork)
    : null;
  const isRecommend = isRecommendMode(mode);

  return {
    mode,
    source,
    sourceCategory: options.sourceCategory || getSourceCategory(source, mode),
    inspectItem: isRecommend ? undefined : source,
    recommendItem: isRecommend ? activeWork || normalizeWorkBuhinWork(source) : undefined,
    seibiList,
    manualSeibiList,
    activeWork,
    selectedWorkIndex: selected.selectedWorkIndex,
    isManualWork: selected.isManualWork,
    buhinList: activeWork ? activeWork.buhinList : [],
    osusumeBuhinList: activeWork ? activeWork.osusumeBuhinList : [],
    manualBuhinList: activeWork ? activeWork.manualBuhinList : [],
    hasSeibiList: seibiList.length > 0,
    isRecommend
  };
}

/*
 * resolveCurrentWork を WorkAndBuhin.vue から分離し、
 * 入力形状の正規化と現在作業解決 を親側置換や単体テストで固定するため。
 */
export function resolveCurrentWork(params: {
  data: any;
  context: WorkBuhinInputContext;
  activeWorkIndex: number;
  isManualWork: boolean;
  editSeibi: boolean;
  activeSeibiNm: string | null;
}): WorkBuhinCurrentWorkResolution {
  if (params.isManualWork && params.editSeibi) {
    const manualSeibiList =
      (params.data && params.data.manualSeibiList) || params.context.manualSeibiList;
    let work = manualSeibiList[params.activeWorkIndex];
    if (work == null && params.activeSeibiNm) {
      const index = manualSeibiList.findIndex(
        (manualSeibi: any) => manualSeibi.seibiNm == params.activeSeibiNm
      );
      if (index > -1) {
        work = manualSeibiList[index];
        return {
          work,
          activeWorkIndex: index,
          shouldUpdateActiveWorkIndex: index !== params.activeWorkIndex
        };
      }
    }
    return {
      work,
      activeWorkIndex: params.activeWorkIndex,
      shouldUpdateActiveWorkIndex: false
    };
  }

  if (params.context.seibiList.length > 0) {
    return {
      work: params.data.seibiList[params.activeWorkIndex],
      activeWorkIndex: params.activeWorkIndex,
      shouldUpdateActiveWorkIndex: false
    };
  }

  return {
    work: params.data,
    activeWorkIndex: params.activeWorkIndex,
    shouldUpdateActiveWorkIndex: false
  };
}

/*
 * buildCurrentWorkEffectState を WorkAndBuhin.vue から分離し、
 * getWork で現在作業解決後に active index を更新する条件と返却 work を固定するため。
 */
export function buildCurrentWorkEffectState(
  resolution: WorkBuhinCurrentWorkResolution
): WorkBuhinCurrentWorkEffectState {
  return {
    shouldUpdateActiveWorkIndex: resolution.shouldUpdateActiveWorkIndex,
    activeWorkIndex: resolution.activeWorkIndex,
    work: resolution.work
  };
}

/*
 * normalizeWorkBuhinWork を WorkAndBuhin.vue から分離し、
 * 入力形状の正規化と現在作業解決 を親側置換や単体テストで固定するため。
 */
export function normalizeWorkBuhinWork(work: any): any {
  if (!work) {
    return null;
  }
  return {
    ...work,
    buhinList: toArray(work.buhinList),
    osusumeBuhinList: toArray(work.osusumeBuhinList),
    manualBuhinList: toArray(work.manualBuhinList)
  };
}

/*
 * toArray を WorkAndBuhin.vue から分離し、
 * 入力形状の正規化と現在作業解決 を親側置換や単体テストで固定するため。
 */
export function toArray<T = any>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value.slice() : [];
}

function getSeibiList(source: any, mode: WorkBuhinSourceMode): any[] {
  if (!source) {
    return [];
  }
  if (mode === "partSelectionRecommend") {
    if (Array.isArray(source.seibiList)) {
      return source.seibiList.slice();
    }
    return toArray(source.subItems);
  }
  return toArray(source.seibiList);
}

function resolveSelectedWork(
  source: any,
  mode: WorkBuhinSourceMode,
  seibiList: any[],
  manualSeibiList: any[],
  options: WorkBuhinNormalizeOptions
): { activeWork: any | null; selectedWorkIndex: number; isManualWork: boolean } {
  const requestedIndex = normalizeIndex(options.selectedWorkIndex);
  const requestedManual = options.isManualWork === true;

  if (requestedManual) {
    return {
      activeWork: manualSeibiList[requestedIndex] || null,
      selectedWorkIndex: requestedIndex,
      isManualWork: true
    };
  }

  if (mode === "partSelectionRecommend") {
    return {
      activeWork: seibiList[requestedIndex] || null,
      selectedWorkIndex: requestedIndex,
      isManualWork: false
    };
  }

  if (seibiList.length > 0 && manualSeibiList.length === 0) {
    const autoIndex = findSelectedWorkIndex(seibiList);
    const selectedWorkIndex = options.selectedWorkIndex == null
      ? autoIndex
      : requestedIndex;
    return {
      activeWork: seibiList[selectedWorkIndex] || null,
      selectedWorkIndex,
      isManualWork: false
    };
  }

  if (manualSeibiList.length > 0) {
    return {
      activeWork: manualSeibiList[requestedIndex] || null,
      selectedWorkIndex: requestedIndex,
      isManualWork: true
    };
  }

  if (source) {
    return {
      activeWork: source,
      selectedWorkIndex: requestedIndex,
      isManualWork: false
    };
  }

  return {
    activeWork: null,
    selectedWorkIndex: requestedIndex,
    isManualWork: false
  };
}

function findSelectedWorkIndex(seibiList: any[]): number {
  const index = seibiList.findIndex((item: any) => item && item.buhinSelected > 0);
  return index === -1 ? 0 : index;
}

function normalizeIndex(index: number | undefined): number {
  if (typeof index !== "number" || index < 0) {
    return 0;
  }
  return index;
}

function isRecommendMode(mode: WorkBuhinSourceMode): boolean {
  return (
    mode === "recommend" ||
    mode === "jizenRecommend" ||
    mode === "partSelectionRecommend"
  );
}

function getSourceCategory(source: any, mode: WorkBuhinSourceMode): any {
  if (mode === "partSelectionRecommend") {
    return source || null;
  }
  return undefined;
}
